import React, { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { ErrorBoundary } from './ErrorBoundary';
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}
const widths = {
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-5xl'
};
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  children
}: ModalProps) {
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  const hidden = reduceMotion ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 16
  };
  return <AnimatePresence data-id="element-349" data-mp="2rarnziwaxqz5">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} data-id="element-350" data-mp="2r9yn2yk0xvox">
          <div className="absolute inset-0 bg-black/85" onClick={onClose} aria-hidden="true" data-id="element-351" data-mp="3f0lz9w0z37ha" />
          <motion.div role="dialog" aria-modal="true" aria-label={title} className={`relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-line bg-panel ${widths[size]} sm:rounded-2xl`} initial={hidden} animate={{
        opacity: 1,
        y: 0
      }} exit={hidden} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-352" data-mp="3a0zkhi5hxj6d">
            <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-panel px-6 py-5" data-id="element-353" data-mp="3syk68gcys1mr">
              <div data-id="element-354" data-mp="1w1ob3ypd9g2i">
                <h2 className="font-display text-2xl font-semibold leading-tight text-white" data-id="element-355" data-mp="1w264mnkn9g6q">{title}</h2>
                {subtitle && <p className="mt-0.5 text-sm text-muted" data-id="element-356" data-mp="1hbqin6kem47i">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Cerrar" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line text-muted transition-colors duration-150 hover:border-neon/60 hover:text-neon focus:outline-none focus-visible:ring-2 focus-visible:ring-neon" data-id="element-357" data-mp="1mb31zhc2gt5c">
                <XIcon className="h-4 w-4" data-id="element-358" data-mp="13ch2u8041c42" />
              </button>
            </header>
            <div className="p-6" data-id="element-359" data-mp="2ss6pwclt1eja">
              <ErrorBoundary onReset={onClose} data-id="element-360" data-mp="2ssyjjhvud26x">{children}</ErrorBoundary>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}